import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  BuildingOfficeIcon, 
  MapPinIcon, 
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { clientsService } from '../services/logistics/clients';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const CreateClient = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams(); // For edit mode
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(!!id); // Loading when editing
  const isEditMode = !!id;

  const gccCountries = [
    { value: 'saudi', label: 'Saudi Arabia', code: '+966' },
    { value: 'uae', label: 'United Arab Emirates', code: '+971' },
    { value: 'kuwait', label: 'Kuwait', code: '+965' },
    { value: 'qatar', label: 'Qatar', code: '+974' },
    { value: 'bahrain', label: 'Bahrain', code: '+973' },
    { value: 'oman', label: 'Oman', code: '+968' }
  ];

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '+966',
    address: '',
    city: '',
    country: 'saudi',
    postalCode: '',
    website: '',
    taxId: '',
    notes: '',
    paymentTerms: '30',
    creditLimit: '',
    industry: '',
    preferredService: 'standard',
    status: 'active'
  });

  // Load client data for editing
  useEffect(() => {
    const loadClientData = async () => {
      if (!isEditMode || !id) return;
      
      setPageLoading(true);
      try {
        const clientData = await clientsService.getClient(id, currentCompany?.id);
        if (clientData) {
          setFormData({
            companyName: clientData.companyName || '',
            contactPerson: clientData.contactPerson || '',
            email: clientData.email || '',
            phone: clientData.phone || '+966',
            address: clientData.address || '',
            city: clientData.city || '',
            country: clientData.country || 'saudi',
            postalCode: clientData.postalCode || '',
            website: clientData.website || '',
            taxId: clientData.taxId || '',
            notes: clientData.notes || '',
            paymentTerms: clientData.paymentTerms || '30',
            creditLimit: clientData.creditLimit || '',
            industry: clientData.industry || '',
            preferredService: clientData.preferredService || 'standard',
            status: clientData.status || 'active'
          });
        }
      } catch (error) {
        console.error('Error loading client:', error);
        toast.error('Failed to load client data');
        navigate('/clients');
      } finally {
        setPageLoading(false);
      }
    };

    loadClientData();
  }, [id, isEditMode, navigate]);

  // Generate unique client ID for new clients
  const generateClientId = async () => {
    try {
      const clients = await clientsService.getClients(currentCompany.id);
      const existingIds = clients
        .map(client => client.clientId)
        .filter(id => id && id.startsWith('C-'))
        .map(id => parseInt(id.split('-')[1]))
        .filter(num => !isNaN(num));
      
      const nextNumber = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
      return `C-${nextNumber.toString().padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating client ID:', error);
      return `C-${Date.now().toString().slice(-3)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'country') {
      const selectedCountry = gccCountries.find(c => c.value === value);
      if (selectedCountry && formData.phone.includes('+')) {
        const phoneWithoutCode = formData.phone.split('-').slice(1).join('-');
        setFormData(prev => ({
          ...prev,
          [name]: value,
          phone: phoneWithoutCode ? `${selectedCountry.code}-${phoneWithoutCode}` : selectedCountry.code
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    const required = ['companyName', 'contactPerson', 'phone'];
    const missing = required.filter(field => !formData[field] || formData[field] === '+966');
    
    if (missing.length > 0) {
      toast.error(t('forms.please_fill_required'));
      return false;
    }

    // Validate email only if provided
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error(t('forms.valid_email_required'));
        return false;
      }
    }

    // Allow any phone format - just check it's not empty since it's required
    if (!formData.phone || formData.phone.trim() === '' || formData.phone === '+966') {
      toast.error(t('forms.phone_required'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    if (!currentCompany?.id) {
      console.error('No company selected. Current company:', currentCompany);
      toast.error('No company selected. Please refresh the page and try again, or contact support if the issue persists.');
      return;
    }

    setLoading(true);
    
    try {
      if (isEditMode) {
        // Update existing client
        const updateData = {
          companyName: formData.companyName,
          contactPerson: formData.contactPerson,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          country: formData.country,
          postalCode: formData.postalCode,
          website: formData.website,
          taxId: formData.taxId,
          notes: formData.notes,
          paymentTerms: formData.paymentTerms,
          creditLimit: formData.creditLimit,
          industry: formData.industry,
          preferredService: formData.preferredService,
          status: formData.status
        };

        // Check if client has all mandatory information to remove quick-created flag
        const hasMandatoryInfo = formData.companyName && 
                                formData.contactPerson && 
                                formData.phone && 
                                formData.phone !== '+966';

        if (hasMandatoryInfo) {
          // Remove the isQuickCreated flag if all mandatory info is provided
          updateData.isQuickCreated = false;
        }

        await clientsService.updateClient(id, updateData);
        if (hasMandatoryInfo) {
          toast.success(t('clients.client_updated_completed'));
        } else {
          toast.success(t('forms.client_updated_success', 'Client updated successfully'));
        }
      } else {
        // Create new client
        const clientId = await generateClientId();
        const clientData = {
          companyId: currentCompany.id,
          clientId: clientId, // Add unique client ID
          companyName: formData.companyName,
          contactPerson: formData.contactPerson,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          country: formData.country,
          postalCode: formData.postalCode,
          website: formData.website,
          taxId: formData.taxId,
          notes: formData.notes,
          paymentTerms: formData.paymentTerms,
          creditLimit: formData.creditLimit,
          industry: formData.industry,
          preferredService: formData.preferredService,
          status: formData.status,
          lastShipment: null,
          totalShipments: 0,
          totalRevenue: 0
        };

        await clientsService.addClient(clientData);
        toast.success(t('forms.client_created_success'));
      }
      
      navigate('/clients');
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} client:`, error);
      toast.error(isEditMode ? t('forms.client_update_failed', 'Failed to update client') : t('forms.client_creation_failed'));
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4 rtl:space-x-reverse">
        <button
          onClick={() => navigate('/clients')}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? t('clients.edit_client') : t('clients.add_client')}
          </h1>
          <p className="text-sm text-gray-500">
            {isEditMode ? t('forms.edit_client_subtitle', 'Update client information') : t('forms.add_client_subtitle')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold flex items-center">
                <BuildingOfficeIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                {t('clients.company_information')}
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="label">{t('clients.company_name')} *</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder={t('forms.enter_company_name')}
                  required
                />
              </div>

              <div>
                <label className="label">{t('clients.contact_person')} *</label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder={t('forms.primary_contact_person')}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('clients.email')}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder={t('forms.enter_email')}
                  />
                </div>
                <div>
                  <label className="label">{t('clients.phone')} *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="+966-XX-XXX-XXXX"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="label">{t('clients.website')}</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="https://example.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('clients.industry')}</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="">{t('forms.select_industry')}</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail</option>
                    <option value="technology">Technology</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="construction">Construction</option>
                    <option value="agriculture">Agriculture</option>
                    <option value="food">Food & Beverage</option>
                    <option value="automotive">Automotive</option>
                    <option value="textiles">Textiles</option>
                    <option value="oil_gas">Oil & Gas</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="label">{t('clients.tax_id')}</label>
                  <input
                    type="text"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="Tax Registration Number"
                  />
                </div>
              </div>

              {/* Status field for edit mode */}
              {isEditMode && (
                <div>
                  <label className="label">{t('common.status')}</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="active">{t('common.active')}</option>
                    <option value="inactive">{t('common.inactive')}</option>
                    <option value="pending">{t('common.pending')}</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold flex items-center">
                <MapPinIcon className="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" />
                {t('clients.address_information')}
              </h3>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="label">{t('clients.country')}</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  {gccCountries.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">{t('clients.city')}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder={t('forms.enter_city')}
                />
              </div>

              <div>
                <label className="label">{t('clients.address')}</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="input-field"
                  rows="3"
                  placeholder={t('forms.enter_full_address')}
                />
              </div>

              <div>
                <label className="label">{t('clients.postal_code')}</label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="12345"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('clients.payment_terms')}</label>
                  <select
                    name="paymentTerms"
                    value={formData.paymentTerms}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="15">15 Days</option>
                    <option value="30">30 Days</option>
                    <option value="45">45 Days</option>
                    <option value="60">60 Days</option>
                    <option value="90">90 Days</option>
                  </select>
                </div>
                <div>
                  <label className="label">{t('clients.credit_limit')}</label>
                  <input
                    type="number"
                    name="creditLimit"
                    value={formData.creditLimit}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="50000"
                  />
                </div>
              </div>

              <div>
                <label className="label">{t('clients.preferred_service')}</label>
                <select
                  name="preferredService"
                  value={formData.preferredService}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value="standard">Standard Delivery</option>
                  <option value="express">Express Delivery</option>
                  <option value="economy">Economy Delivery</option>
                  <option value="priority">Priority Service</option>
                </select>
              </div>

              <div>
                <label className="label">{t('accounting.notes')}</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="input-field"
                  rows="3"
                  placeholder={t('forms.additional_notes')}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4 rtl:space-x-reverse">
          <button
            type="button"
            onClick={() => navigate('/clients')}
            className="btn btn-outline"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <LoadingSpinner />
                <span>{isEditMode ? t('common.updating') : t('common.creating')}</span>
              </div>
            ) : (
              isEditMode ? t('common.update') : t('common.create')
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateClient; 